import { IPaginatedListData } from '@/services/types';
import {
  ContractContext,
  IContractActions,
  IContractItemDetailActionPayload,
  IContractConfirmRenewalDetailActionPayload,
  IContractAttachmentDetailPayload,
  IContractItemUpdateActionPayload,
  IContractFetchActionPayload,
  IContractAttachmentCreateActionPayload,
  IContractUpdateStateActionPayload
} from './actions.types';
import { IContractItemData, IContractData } from '@/models';

export const actions: IContractActions = {
  fetchContracts(
    context: ContractContext,
    payload: IContractFetchActionPayload
  ): Promise<void> {
    const promise = this.$services.contract.fetchContracts(payload);
    context.commit('SET_LAST_PROMISE', promise);

    return promise.then((paginated: IPaginatedListData<IContractData>) => {
      if (promise == context.state.lastPromise) {
        context.commit('SET_COUNT', paginated.count);
        context.commit('SET_CONTRACTS', paginated.results);
      }
      context.commit('SET_LAST_PROMISE', null);
    });
  },

  fetchContract(context: ContractContext, payload: number): Promise<void> {
    return this.$services.contract
      .fetchContract(payload)
      .then((contract: IContractData) => {
        const index = context.state.contracts.findIndex(
          (v: IContractData) => v.id === contract.id
        );
        if (index === -1) {
          context.commit('ADD_CONTRACT', contract);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_CONTRACT', contract);
        }
      });
  },

  createContract(
    context: ContractContext,
    payload: IContractData
  ): Promise<number> {
    return this.$services.contract
      .createContract(payload)
      .then((contract: IContractData) => {
        context.commit('ADD_CONTRACT', contract);
        context.commit('INCREMENT_COUNT');

        return contract.id!;
      });
  },

  updateContract(
    context: ContractContext,
    payload: IContractData
  ): Promise<void> {
    return this.$services.contract
      .updateContract(payload)
      .then((contract: IContractData) => {
        const index = context.state.contracts.findIndex(
          (v: IContractData) => v.id === contract.id
        );
        if (index === -1) {
          context.commit('ADD_CONTRACT', contract);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_CONTRACT', contract);
        }
      });
  },

  deleteContract(context: ContractContext, payload: number): Promise<void> {
    return this.$services.contract.deleteContract(payload).then(() => {
      context.commit('REMOVE_CONTRACT', payload);
      context.commit('DECREMENT_COUNT');
    });
  },

  updateContractState(
    context: ContractContext,
    payload: IContractUpdateStateActionPayload
  ): Promise<void> {
    return this.$services.contract
      .updateContractState(payload.contractId, payload.state)
      .then((contract: IContractData) => {
        const index = context.state.contracts.findIndex(
          (v: IContractData) => v.id === contract.id
        );
        if (index === -1) {
          context.commit('ADD_CONTRACT', contract);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_CONTRACT', contract);
        }
      });
  },

  initiateContractRenewal(
    context: ContractContext,
    payload: number
  ): Promise<void> {
    return this.$services.contract
      .initiateContractRenewal(payload)
      .then((contract: IContractData) => {
        const index = context.state.contracts.findIndex(
          (v: IContractData) => v.id === contract.id
        );
        if (index === -1) {
          context.commit('ADD_CONTRACT', contract);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_CONTRACT', contract);
        }
      });
  },

  confirmRenewal(
    context: ContractContext,
    payload: IContractConfirmRenewalDetailActionPayload
  ): Promise<void> {
    return this.$services.contract
      .confirmRenewal(payload.contractId, payload.confirmationType)
      .then((contract: IContractData) => {
        const index = context.state.contracts.findIndex(
          (v: IContractData) => v.id === contract.id
        );
        if (index === -1) {
          context.commit('ADD_CONTRACT', contract);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_CONTRACT', contract);
        }
      });
  },

  fetchContractItems(context: ContractContext, payload: number): Promise<void> {
    return this.$services.contract
      .fetchContractItems(payload)
      .then((items: IContractItemData[]) => {
        context.commit('SET_CONTRACT_ITEMS_FOR_CONTRACT', {
          contractId: payload,
          items
        });
      });
  },

  fetchContractItem(
    context: ContractContext,
    payload: IContractItemDetailActionPayload
  ): Promise<void> {
    return this.$services.contract
      .fetchContractItem(payload.contractId, payload.itemId)
      .then((item: IContractItemData) => {
        context.commit('UPSERT_CONTRACT_ITEM_IN_CONTRACT', {
          item,
          contractId: payload.contractId
        });
      });
  },

  createContractItem(
    context: ContractContext,
    payload: IContractItemUpdateActionPayload
  ): Promise<void> {
    return this.$services.contract
      .createContractItem(payload.contractId, payload.item)
      .then((item: IContractItemData) => {
        context.commit('UPSERT_CONTRACT_ITEM_IN_CONTRACT', {
          contractId: payload.contractId,
          item
        });
      });
  },

  updateContractItem(
    context: ContractContext,
    payload: IContractItemUpdateActionPayload
  ): Promise<void> {
    return this.$services.contract
      .updateContractItem(payload.contractId, payload.item)
      .then((item: IContractItemData) => {
        context.commit('UPSERT_CONTRACT_ITEM_IN_CONTRACT', {
          contractId: payload.contractId,
          item
        });
      });
  },

  deleteContractItem(
    context: ContractContext,
    payload: IContractItemDetailActionPayload
  ): Promise<void> {
    return this.$services.contract
      .deleteContractItem(payload.contractId, payload.itemId)
      .then(() => {
        context.commit('REMOVE_CONTRACT_ITEM_FROM_CONTRACT', {
          contractId: payload.contractId,
          itemId: payload.itemId
        });
      });
  },

  addContractAttachment(
    context: ContractContext,
    payload: IContractAttachmentCreateActionPayload
  ): Promise<void> {
    return this.$services.contract
      .addContractAttachment(payload.contractId, payload.attachment)
      .then(contract => {
        const index = context.state.contracts.findIndex(
          item => item.id === contract.id
        );
        if (index === -1) {
          context.commit('ADD_CONTRACT', contract);
        } else {
          context.commit('UPDATE_CONTRACT', contract);
        }
      });
  },

  removeContractAttachment(
    context: ContractContext,
    payload: IContractAttachmentDetailPayload
  ): Promise<void> {
    return this.$services.contract
      .removeContractAttachment(payload.contractId, payload.attachmentId)
      .then(() => {
        const contract = context.state.contracts.find(
          contract => contract.id === payload.contractId
        );

        if (contract) {
          const attachmentIndex = contract.files!.findIndex(
            attachment => attachment.id === payload.attachmentId
          );

          contract.files!.splice(attachmentIndex, 1);

          context.commit('UPDATE_CONTRACT', contract);
        }
      });
  },

  downloadContractAttachment(
    _context: ContractContext,
    payload: IContractAttachmentDetailPayload
  ): Promise<Blob> {
    return this.$services.contract.downloadContractAttachment(
      payload.contractId,
      payload.attachmentId
    );
  }
};
